/**
 * Plain decoding of an ID token payload — NO signature check. `IdTokenVerifier` does that,
 * once, at the moment a token enters the app.
 *
 * These helpers exist for tokens that were already verified and then persisted: re-running
 * the full check at startup would need the JWKS, and therefore a network connection, which
 * would defeat the offline-capable start.
 *
 * Anything that actually protects data must still be enforced by the backend — a
 * client-side role check is a convenience, not a security boundary.
 */

interface IdTokenClaims {
  sub?: unknown;
  subname?: unknown;
  memberOf?: unknown;
  eduPersonAffiliation?: unknown;
  employeeType?: unknown;
  employeeNumber?: unknown;
  'x-de-tudortmund-matr'?: unknown;
  given_name?: unknown;
  name?: unknown;
  family_name?: unknown;
}

const decodePayload = (idToken: string): IdTokenClaims | null => {
  const payload = idToken.split('.')[1];
  if (!payload) return null;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    // atob tolerates missing padding in some engines but not all, so pad explicitly.
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const json = new TextDecoder().decode(
      Uint8Array.from(atob(padded), character => character.charCodeAt(0)),
    );
    return JSON.parse(json) as IdTokenClaims;
  } catch {
    return null;
  }
};

/** A claim may arrive as an array or, when it holds a single value, as a bare string. */
const toStringList = (claim: unknown): string[] => {
  if (typeof claim === 'string') return [claim];
  if (Array.isArray(claim)) return claim.filter((v): v is string => typeof v === 'string');
  return [];
};

/** Numbers are accepted too: identifiers like the employee number may arrive unquoted. */
const toOptionalString = (claim: unknown): string | undefined => {
  if (typeof claim === 'string') return claim;
  if (typeof claim === 'number') return String(claim);
  return undefined;
};

/**
 * ForgeRock AM issues `sub` in its "compressed JSON" form `(type!name)` — for a user
 * identity `(usr!mtimschr)` — so subjects stay unique across identity types: a user and
 * an OAuth2 client sharing a name must not end up with the same subject. The plain
 * account name travels in the `subname` claim AM adds for exactly this purpose;
 * unwrapping `sub` ourselves is only the fallback for a token without that claim.
 */
const toUsername = (claims: IdTokenClaims): string | undefined => {
  const subname = toOptionalString(claims.subname);
  if (subname) return subname;
  const sub = toOptionalString(claims.sub);
  // A `sub` that is not in the compressed form is the account name itself (pre-AM-7 style).
  return sub?.match(/^\(usr!(.+)\)$/)?.[1] ?? sub;
};

/** Subject of an already verified token, to check that a refresh did not change identity. */
export const readSubject = (idToken: string | null): string | null => {
  if (!idToken) return null;
  const sub = decodePayload(idToken)?.sub;
  return typeof sub === 'string' ? sub : null;
};

/**
 * Returns null when the roles cannot be determined — no ID token, or a payload that will
 * not decode. Callers must treat that as "unknown", not as "no roles": stripping
 * role-gated tiles while a token refresh is still in flight would remove tiles the user
 * regains a moment later.
 */
export const extractRoles = (idToken: string | undefined): readonly string[] | null => {
  if (!idToken) return null;
  const claims = decodePayload(idToken);
  if (!claims) return null;

  return [
    ...toStringList(claims.memberOf),
    ...toStringList(claims.eduPersonAffiliation),
    ...toStringList(claims.employeeType),
  ];
};

/**
 * Every field is optional: which claims the IdP includes depends on the granted scope and
 * on the account — a student has no `employeeNumber`, staff no matriculation number.
 * Consumers must handle each field being absent rather than expecting one from the other.
 */
export interface IdTokenProfile {
  /** Raw `sub` exactly as issued — opaque, only good for comparing tokens with each other. */
  subject?: string;
  /** The plain account name (`mtimschr`), without AM's `(usr!…)` wrapping — see `toUsername`. */
  username?: string;
  givenName?: string;
  familyName?: string;
  /** The full display name as the IdP composes it, not necessarily "given family". */
  name?: string;
  employeeNumber?: string;
  /** Claim `x-de-tudortmund-matr` — only students carry one. */
  matriculationNumber?: string;
  /** A single value at this IdP. The same claim also feeds the roles. */
  employeeType?: string;
}

/** Null when there is no token or its payload will not decode — then nothing is known. */
export const extractProfile = (idToken: string | undefined): IdTokenProfile | null => {
  if (!idToken) return null;
  const claims = decodePayload(idToken);
  if (!claims) return null;

  return {
    subject: toOptionalString(claims.sub),
    username: toUsername(claims),
    givenName: toOptionalString(claims.given_name),
    familyName: toOptionalString(claims.family_name),
    name: toOptionalString(claims.name),
    employeeNumber: toOptionalString(claims.employeeNumber),
    matriculationNumber: toOptionalString(claims['x-de-tudortmund-matr']),
    employeeType: toOptionalString(claims.employeeType),
  };
};
